import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import {
  PICKEM_SPORTS, DEFAULT_FILTERS,
  formatSelection, getEVForLegs, getThresholdOdds,
  getHoursUntilStart,
} from './pickEmConfig'
import PickEmFilterPanel from './PickEmFilterPanel'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import PickEmMultiBuilder from './PickEmMultiBuilder'
import PickEmHiddenModal from './PickEmHiddenModal'
import PickEmCard from './PickEmCard'
import PickEmTour from './pickemTour'
import PreviewWall from '../components/PreviewWall'
import '../styles/app.css'
import '../styles/app-mobile.css'
import useVisiblePolling from '../hooks/useVisiblePolling'

const PAGE_TYPE = 'pickem'

const formatTimeWithSeconds = (ts) => {
  if (!ts) return ''
  const d = new Date(ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z')
  if (isNaN(d)) return ''
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
}

const shortSideLabel = (selection) => {
  if (!selection) return ''
  const m = selection.match(/(over|under)\s+(-?\d+(?:\.\d+)?)/i)
  if (m) return `${m[1].charAt(0).toUpperCase()}${m[1].slice(1).toLowerCase()} ${m[2]}`
  return selection
}

export default function PickEmMobile({ isVisible = true, isPreview = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('pickem')

  // ─── Data ──────────────────────────────────────────────────────────────────
  const [opportunities,      setOpportunities]      = useState([])
  const [loading,            setLoading]            = useState(true)
  const [error,              setError]              = useState(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)

  // ─── UI ────────────────────────────────────────────────────────────────────
  const [expandedCard,    setExpandedCard]    = useState(null)
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [sortBy,          setSortBy]          = useState('ev')
  const [sortDir,         setSortDir]         = useState('desc')
  const [currentPage,     setCurrentPage]     = useState(1)
  const itemsPerPage = 25

  // ─── Filters ───────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    sports: new Set(DEFAULT_FILTERS.sports),
  }))

  // ─── Saved filters ─────────────────────────────────────────────────────────
  const [savedFilters,        setSavedFilters]        = useState([])
  const [filterName,          setFilterName]          = useState('')
  const [activeFilterId,      setActiveFilterId]      = useState(null)
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)
  const loadingFilterRef = useRef(false)

  // ─── Hidden bets ───────────────────────────────────────────────────────────
  const [hiddenBets,      setHiddenBets]      = useState({})
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  // ─── Multi builder ─────────────────────────────────────────────────────────
  const [showMultiModal, setShowMultiModal] = useState(false)
  const [selectedLegs,   setSelectedLegs]   = useState([])
  const [multiError,     setMultiError]     = useState(null)

  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('pickem-proxy')
      if (error) throw error
      const opps = Array.isArray(data) ? data : (data.opportunities || data.data || [])
      setOpportunities(opps)
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // ─── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => { fetchOpportunities() }, [])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('hidden-pickem-bets') || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([id, data]) => {
      if (data.commence_time) {
        const gameTime = new Date(data.commence_time + 'Z')
        if (now < new Date(gameTime.getTime() + 60 * 60 * 1000)) cleaned[id] = data
      }
    })
    localStorage.setItem('hidden-pickem-bets', JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  useEffect(() => {
    if (!user?.id) return
    getUserFilters(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({
        id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data
      })))
    })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) applyFilterData(data.filter_data)
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  useEffect(() => { setCurrentPage(1) }, [filters, sortBy, sortDir])

  useEffect(() => {
    if (loadingFilterRef.current) { loadingFilterRef.current = false; return }
    setActiveFilterId(null)
  }, [filters])

  // ─── Filter helpers ────────────────────────────────────────────────────────
  const applyFilterData = (fd) => {
    if (!fd?.filters) return
    loadingFilterRef.current = true
    // Prefer the saved excludedSports blacklist (newer filters) so sports added after the
    // filter was saved stay visible (selected = all minus excluded). Fall back to the legacy
    // sports whitelist for old filters.
    const selectedSports = Array.isArray(fd.filters.excludedSports)
      ? PICKEM_SPORTS.filter(s => !fd.filters.excludedSports.includes(s))
      : (fd.filters.sports || PICKEM_SPORTS)
    setFilters({
      sports:     new Set(selectedSports),
      ev:         fd.filters.ev         || DEFAULT_FILTERS.ev,
      hoursUntil: fd.filters.hoursUntil || DEFAULT_FILTERS.hoursUntil,
      minBooks:   fd.filters.minBooks   ?? DEFAULT_FILTERS.minBooks,
      legCount:   fd.filters.legCount   || DEFAULT_FILTERS.legCount,
    })
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    const fd = {
      filters: {
        sports:         [...filters.sports],                                  // legacy whitelist (back-compat)
        excludedSports: PICKEM_SPORTS.filter(s => !filters.sports.has(s)),    // blacklist = source of truth on load
        ev:         filters.ev,
        hoursUntil: filters.hoursUntil,
        minBooks:   filters.minBooks,
        legCount:   filters.legCount,
      },
      createdAt: new Date().toISOString(),
    }
    const { data, error } = await saveUserFilter(user.id, PAGE_TYPE, name.trim(), fd)
    if (error) { alert('Error saving filter: ' + error); return }
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter   = (saved) => { applyFilterData(saved); setActiveFilterId(saved.id) }

  const handleDeleteFilter = async (id) => {
    if (!user?.id) return
    await deleteUserFilter(id)
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }

  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (!current) return
    if (current.isDefault) {
      await unsetDefaultFilter(user.id, PAGE_TYPE, id)
    } else {
      await setDefaultFilter(user.id, PAGE_TYPE, id)
    }
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current.isDefault : false })))
  }

  // ─── Hidden bets helpers ──────────────────────────────────────────────────
  const hideBetId = (opp) => `${opp.player_name}_${opp.market_type}`.replace(/[^a-zA-Z0-9_]/g, '_')
  const isHidden  = (opp) => !!hiddenBets[hideBetId(opp)]

  const hideOpp = (opp) => {
    const updated = {
      ...hiddenBets,
      [hideBetId(opp)]: {
        player_name:   opp.player_name,
        market_type:   opp.market_type,
        selection:     formatSelection(opp.selection, opp.market_type),
        match:         opp.match,
        sport:         opp.sport,
        commence_time: opp.commence_time,
        ev:            getEVForLegs(opp.ev_by_legs, filters.legCount).ev,
      }
    }
    localStorage.setItem('hidden-pickem-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpp = (betId) => {
    const updated = { ...hiddenBets }
    delete updated[betId]
    localStorage.setItem('hidden-pickem-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAllHidden = () => {
    localStorage.removeItem('hidden-pickem-bets')
    setHiddenBets({})
  }

  // ─── Multi builder helpers ────────────────────────────────────────────────
  const legKey    = (opp) => opp._id
  const isInMulti = (opp) => selectedLegs.some(l => l._key === legKey(opp))

  const flashMultiError = (key, msg) => {
    setMultiError({ key, msg })
    setTimeout(() => setMultiError(prev => prev?.key === key ? null : prev), 2800)
  }

  const addLeg = (opp) => {
    setSelectedLegs(prev => [...prev, {
      _key:          legKey(opp),
      player_name:   opp.player_name,
      selection:     formatSelection(opp.selection, opp.market_type),
      match:         opp.match,
      market_type:   opp.market_type,
      sport:         opp.sport,
      commence_time: opp.commence_time,
      fair_odds:     opp.fair_odds,
      ev_by_legs:    opp.ev_by_legs,
      game_url:      opp.game_url || null,
      // Teams and the raw values behind the selection, so a multi's legs can be
      // settled against results without parsing the display string back apart.
      ...settlementFields(opp),
    }])
  }

  const toggleLeg = (opp) => {
    if (isInMulti(opp)) {
      setSelectedLegs(prev => prev.filter(l => l._key !== legKey(opp)))
      return
    }
    if (selectedLegs.some(l => l.player_name === opp.player_name)) {
      flashMultiError(legKey(opp), `${opp.player_name} already in multi`)
      return
    }
    addLeg(opp)
  }

  // ─── Sort + filter ────────────────────────────────────────────────────────
  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opp => {
      if (!filters.sports.has(opp.sport)) return false
      const hours = getHoursUntilStart(opp.commence_time)
      if (hours < filters.hoursUntil[0] || hours > filters.hoursUntil[1]) return false
      if ((opp.bookmaker_count || 0) < filters.minBooks) return false
      const { ev } = getEVForLegs(opp.ev_by_legs, filters.legCount)
      if (ev === null) return false
      if (ev < filters.ev[0] || ev > filters.ev[1]) return false
      return true
    })
  }, [opportunities, filters])

  const sortedOpportunities = useMemo(() => {
    return [...filteredOpportunities].sort((a, b) => {
      let av, bv
      if      (sortBy === 'ev')            { av = getEVForLegs(a.ev_by_legs, filters.legCount).ev || 0; bv = getEVForLegs(b.ev_by_legs, filters.legCount).ev || 0 }
      else if (sortBy === 'fair_odds')     { av = a.fair_odds || 0;       bv = b.fair_odds || 0 }
      else if (sortBy === 'win_prob')        { av = a.win_probability || 0; bv = b.win_probability || 0 }
      else if (sortBy === 'commence_time')   { av = new Date(a.commence_time + 'Z').getTime(); bv = new Date(b.commence_time + 'Z').getTime() }
      else if (sortBy === 'bookmaker_count') { av = a.bookmaker_count || 0; bv = b.bookmaker_count || 0 }
      else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })
  }, [filteredOpportunities, sortBy, sortDir, filters.legCount])

  const visibleOpps = useMemo(
    () => sortedOpportunities.filter(o => !isHidden(o)),
    // isHidden closes over hiddenBets (already a dep); listing it would recompute every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedOpportunities, hiddenBets]
  )

  const totalPages = Math.max(1, Math.ceil(visibleOpps.length / itemsPerPage))
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1) }, [totalPages, currentPage])
  const pagedOpps = isPreview
    ? visibleOpps.slice(0, 4)
    : visibleOpps.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  if (!isVisible) return null

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bcm-container">

      {/* Header */}
      <div className="bcm-header">
        <div className="bcm-action-row">

          <div className="bcm-action-left">
            <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters
            </button>

            {!isPreview && (
              <button
                className={`bc-btn bc-btn-toggle${selectedLegs.length > 0 ? ' toggle-active' : ''}`}
                onClick={() => setShowMultiModal(true)}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
                {selectedLegs.length > 0 && <span className="bc-multi-count">{selectedLegs.length}</span>}
              </button>
            )}

            {Object.keys(hiddenBets).length > 0 && (
              <button className="bc-btn" onClick={() => setShowHiddenModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                {Object.keys(hiddenBets).length}
              </button>
            )}
          </div>

          <div className="bcm-action-right">
            <button
              className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
              onClick={() => setAutoRefreshEnabled(v => !v)}
            >
              Auto
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      {error && <div className="bc-error" style={{ margin: '12px' }}>Error: {error}</div>}

      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {/* Cards */}
      {!loading && (
        <div className="bcm-cards">
          {pagedOpps.map((opp, idx) => {
            const blurCard   = isPreview && idx > 0
            const blurStyle  = blurCard ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}
            const oppKey     = legKey(opp)
            const inMulti    = isInMulti(opp)
            const legIdx     = inMulti ? selectedLegs.findIndex(l => l._key === oppKey) : -1
            const isExpanded = expandedCard === oppKey
            const { ev }     = getEVForLegs(opp.ev_by_legs, filters.legCount)
            const threshold  = getThresholdOdds(filters.legCount)

            const bookEntries = Object.entries(opp.all_bookmaker_odds || {})
              .sort((a, b) => (b[1].odds || 0) - (a[1].odds || 0))
            const hasOpposing  = bookEntries.some(([, d]) => typeof d.opposing_odds === 'number')
            const topOdds      = bookEntries[0]?.[1].odds
            const bestSet      = new Set(bookEntries.filter(([, d]) => d.odds === topOdds).map(([b]) => b))
            const topOpposing  = hasOpposing ? Math.max(...bookEntries.map(([, d]) => d.opposing_odds || 0)) : null
            const bestOpposing = hasOpposing
              ? new Set(bookEntries.filter(([, d]) => d.opposing_odds === topOpposing).map(([b]) => b))
              : new Set()
            const oppEntry    = hasOpposing ? bookEntries.find(([, d]) => d.opposing_selection) : null
            const pickedLabel = shortSideLabel(opp.selection)
            const pairedLabel = oppEntry ? shortSideLabel(oppEntry[1].opposing_selection) : 'Paired'

            return (
              <React.Fragment key={oppKey}>
                <PickEmCard
                  opp={opp}
                  ev={ev}
                  threshold={threshold}
                  legCount={filters.legCount}
                  style={blurStyle}
                  blurPick={isPreview && idx === 0}
                  inMulti={inMulti}
                  legIdx={legIdx}
                  isExpanded={isExpanded}
                  showButtons={!isPreview}
                  onOpenDabble={() => !isPreview && opp.game_url && window.open(opp.game_url, '_blank')}
                  onMulti={() => toggleLeg(opp)}
                  onExpand={() => setExpandedCard(isExpanded ? null : oppKey)}
                  onHide={() => hideOpp(opp)}
                >
                  {/* Expanded: bookmaker odds + EV per leg count */}
                  {isExpanded && (
                    <div className="bcm-odds-dropdown">
                      {bookEntries.length > 0 && (
                        <>
                          <div className="bcm-odds-header">
                            <span className="bcm-odds-label over">{pickedLabel || 'Over'}</span>
                            <span />
                            {hasOpposing && <span className="bcm-odds-label under">{pairedLabel}</span>}
                          </div>
                          {bookEntries.map(([bName, data]) => {
                            const isBestOver = bestSet.has(bName)
                            const isBestOpp  = bestOpposing.has(bName)
                            const isBestBook = isBestOver || isBestOpp
                            return (
                              <div key={bName} className={`bcm-odds-row${isBestBook ? ' best-row' : ''}`}>
                                <span className={`bcm-odds-val${isBestOver ? ' is-best' : ''}`}>
                                  {typeof data.odds === 'number' ? convertOdds(data.odds, oddsFormat) : '—'}
                                </span>
                                {BOOKMAKER_LOGOS[bName]
                                  ? <img src={BOOKMAKER_LOGOS[bName]} alt={bName} className={`bcm-odds-logo${isBestBook ? ' is-best-book' : ''}`} />
                                  : <span className={`bcm-odds-bname${isBestBook ? ' is-best-book' : ''}`}>{bName}</span>
                                }
                                {hasOpposing
                                  ? <span className={`bcm-odds-val${isBestOpp ? ' is-best' : ''}`}>
                                      {typeof data.opposing_odds === 'number' ? convertOdds(data.opposing_odds, oddsFormat) : '—'}
                                    </span>
                                  : <span />
                                }
                              </div>
                            )
                          })}
                        </>
                      )}

                      {/* Stats: fair odds + win prob + EV per leg count */}
                      <div className="bcm-stats-grid">
                        {typeof opp.fair_odds === 'number' && (
                          <div className="bcm-stat">
                            <span className="bcm-stat-label">Fair Odds</span>
                            <span className="bcm-stat-val">{convertOdds(opp.fair_odds, oddsFormat)}</span>
                          </div>
                        )}
                        {typeof opp.win_probability === 'number' && (
                          <div className="bcm-stat">
                            <span className="bcm-stat-label">Win Prob</span>
                            <span className="bcm-stat-val">{opp.win_probability.toFixed(2)}%</span>
                          </div>
                        )}
                        {opp.last_update && (
                          <div className="bcm-stat">
                            <span className="bcm-stat-label">Updated</span>
                            <span className="bcm-stat-val">{formatTimeWithSeconds(opp.last_update)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Multi error tip */}
                  {multiError?.key === oppKey && (
                    <div className="bcm-err-tip">{multiError.msg}</div>
                  )}

                </PickEmCard>

              {/* Preview wall — right below the first (teaser) card */}
              {isPreview && idx === 0 && (
                <PreviewWall
              title={visibleOpps.length > 0
                ? `${visibleOpps.length} Pick'Em opportunities right now`
                : "Pick'Em opportunities right now"}
              features={['Real time odds from all major bookmakers', 'EV calculated for every leg count (2–12)', 'Automatic multi builder with payout calculator', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
              )}
              </React.Fragment>
            )
          })}

          {/* Preview wall fallback — still shows when there are zero opportunities */}
          {isPreview && pagedOpps.length === 0 && (
            <PreviewWall
              title={visibleOpps.length > 0
                ? `${visibleOpps.length} Pick'Em opportunities right now`
                : "Pick'Em opportunities right now"}
              features={['Real time odds from all major bookmakers', 'EV calculated for every leg count (2–12)', 'Automatic multi builder with payout calculator', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
          )}

          {!isPreview && visibleOpps.length === 0 && (
            <div className="bc-empty">No opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bcm-pagination">
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* Floating multi FAB */}
      {!isPreview && selectedLegs.length > 0 && !showMultiModal && (
        <button className="bc-multi-fab bc-multi-fab-mobile" onClick={() => setShowMultiModal(true)} title="Open Multi Builder">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          <span className="bc-multi-fab-label">Multi</span>
          <span className="bc-multi-fab-count">{selectedLegs.length}</span>
        </button>
      )}

      {/* Modals */}
      <PickEmFilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDir={sortDir}
        setSortDir={setSortDir}
      />

      <PickEmMultiBuilder
        isOpen={showMultiModal}
        onClose={() => setShowMultiModal(false)}
        rawOpportunities={opportunities}
        selectedLegs={selectedLegs}
        onLegsChange={setSelectedLegs}
        mobile
      />

      <PickEmHiddenModal
        isOpen={showHiddenModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpp}
        onUnhideAll={clearAllHidden}
        onClose={() => setShowHiddenModal(false)}
      />

      <PickEmTour isVisible={isVisible} isMobile />

    </div>
  )
}
